import React from 'react';
import { XIcon } from 'lucide-react';
export interface TagProps {
  label: string;
  color?: 'blue' | 'green' | 'orange' | 'purple' | 'gray' | string;
  onRemove?: () => void;
  onClick?: () => void;
  selected?: boolean;
  'data-id'?: string;
}
export type TagColor = 'blue' | 'green' | 'orange' | 'purple' | 'gray';
const colorStyles = {
  blue: {
    bg: 'rgba(14, 165, 233, 0.15)',
    text: '#0EA5E9',
    border: 'rgba(14, 165, 233, 0.3)'
  },
  green: {
    bg: 'rgba(16, 185, 129, 0.15)',
    text: '#10B981',
    border: 'rgba(16, 185, 129, 0.3)'
  },
  orange: {
    bg: 'rgba(245, 158, 11, 0.15)',
    text: '#F59E0B',
    border: 'rgba(245, 158, 11, 0.3)'
  },
  purple: {
    bg: 'rgba(111, 66, 193, 0.15)',
    text: '#6F42C1',
    border: 'rgba(111, 66, 193, 0.3)'
  },
  gray: {
    bg: 'rgba(71, 85, 105, 0.15)',
    text: '#475569',
    border: 'rgba(71, 85, 105, 0.3)'
  }
};
// Helper function to convert hex to rgba
const hexToRgba = (hex: string, alpha: number = 1): string => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) return `rgba(14, 165, 233, ${alpha})`; // fallback to blue
  const r = parseInt(result[1], 16);
  const g = parseInt(result[2], 16);
  const b = parseInt(result[3], 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};
// Get styles for a color (predefined or custom hex)
const getColorStyles = (color: string) => {
  // If it's a predefined color, use the colorStyles object
  if (color in colorStyles) {
    return colorStyles[color as keyof typeof colorStyles];
  }
  // If it's a custom hex color, generate styles
  if (color.startsWith('#')) {
    return {
      bg: hexToRgba(color, 0.15),
      text: color,
      border: hexToRgba(color, 0.3)
    };
  }
  // Fallback to blue if color is invalid
  return colorStyles.blue;
};
export function Tag({
  label,
  color = 'blue',
  onRemove,
  onClick,
  selected = false,
  'data-id': dataId
}: TagProps) {
  const styles = getColorStyles(color);
  const isClickable = !!onClick;
  return <span onClick={onClick} className={`
        inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-sm font-medium
        transition-all duration-200
        ${isClickable ? 'cursor-pointer hover:scale-105' : ''}
        ${selected ? 'ring-2 ring-offset-1' : ''}
      `} style={{
    backgroundColor: styles.bg,
    color: styles.text,
    border: `1px solid ${styles.border}`,
    ringColor: selected ? styles.text : undefined
  }} data-id="element-5850" data-mp="3dthu01i0cbwx">
      <span className="leading-none" data-id="element-5851" data-mp="3rshhv6xp4ms1">{label}</span>
      {onRemove && <button type="button" onClick={e => {
      e.stopPropagation();
      onRemove();
    }} className="flex items-center justify-center w-4 h-4 rounded-full hover:bg-black/10 transition-colors" aria-label={`Remove ${label} tag`} data-id="element-5852" data-mp="3rt93boxkx44l">
          <XIcon className="w-3 h-3" data-id="element-5853" data-mp="2be7r665zely1" />
        </button>}
    </span>;
}